import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, Grid, Typography, Box, Avatar, TextField, Pagination, Stack, MenuItem, Select, InputLabel, FormControl, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { Crumbs } from "../Crumbs";
import { Helmet } from "react-helmet";
import SubNav from "./Community/SubNav";

const BACKEND_HOST = process.env["REACT_APP_BACKEND_HOST"];
const PAGE_SIZE = 12;

export const UserProfileListPage = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState("name:asc");

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append("pagination[pageSize]", PAGE_SIZE);
        params.append("pagination[page]", page);
        params.append("sort", sort);
        params.append("populate", "pic");
        if (search) params.append("filters[name][$containsi]", search);

        fetch(`${BACKEND_HOST}/user-profiles?${params.toString()}`)
            .then(res => res.json())
            .then(data => {
                setProfiles(data.data || []);
                setTotal(data.meta?.pagination?.total || 0);
                setLoading(false);
            });
    }, [search, page, sort]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleSortChange = (e) => {
        setSort(e.target.value);
        setPage(1);
    };

    const pageCount = Math.ceil(total / PAGE_SIZE);

    const desc = "Meet the Austin 2-Step community members and explore their profiles.";
    const title = "Austin 2-Step Community Members";

    const meta = <Helmet>
                <title>{title}</title>
                <meta name="description" content={desc} />
                <meta charSet="utf-8"/>
                <meta property="og:description" content={desc}/>
                <meta property="twitter:title" content={title}/>
                <meta property="twitter:description" content={desc}/>
                <title>{title}</title>
                <meta property="description" content={desc} />
                <link rel="canonical" href={"/community/profiles"}/>
            </Helmet>

    return (
        <Box>
            {meta}
            <Box>
                <Crumbs crumbs={[
                    { title: "Community", href: "/community" },
                    { title: "Profiles" }]} />
            </Box>
            <SubNav />
            <Box my={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Search Profiles"
                        value={search}
                        onChange={handleSearchChange}
                        variant="outlined"
                        size="small"
                    />
                    <FormControl size="small" variant="outlined">
                        <InputLabel id="sort-label">Sort By</InputLabel>
                        <Select
                            labelId="sort-label"
                            value={sort}
                            onChange={handleSortChange}
                            label="Sort By"
                        >
                            <MenuItem value="name:asc">Name (A-Z)</MenuItem>
                            <MenuItem value="name:desc">Name (Z-A)</MenuItem>
                            <MenuItem value="createdAt:desc">Newest</MenuItem>
                            <MenuItem value="createdAt:asc">Oldest</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Box>
            {loading ? (
                <Typography>Loading...</Typography>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {profiles.map((profile) => (
                            <Grid item xs={12} md={6} lg={4} key={profile.id}>
                                <Card>
                                    <CardHeader
                                        avatar={
                                            profile?.pic?.formats?.thumbnail?.url ? (
                                                <Avatar
                                                    src={profile.pic.formats.thumbnail.url}
                                                    alt={profile?.name}
                                                    sx={{ width: 56, height: 56 }}
                                                />
                                            ) : (
                                                <Avatar sx={{ width: 56, height: 56 }}>
                                                    {profile?.name?.[0]?.toUpperCase() || "?"}
                                                </Avatar>
                                            )
                                        }
                                        title={profile?.name || "Unnamed User"}
                                        subheader={profile?.instagram ? `@${profile?.instagram}` : ""}
                                    />
                                    <CardContent>
                                        <Typography variant="body2" color="textSecondary">
                                            {profile?.bio || ""}
                                        </Typography>
                                        <Box mt={2}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                component={Link}
                                                to={`/community/profile/${profile.documentId}`}
                                            >
                                                View Profile
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    {pageCount > 1 && (
                        <Box mt={4} display="flex" justifyContent="center">
                            <Pagination
                                count={pageCount}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};